import { AngularLayout, FlexLayout } from '@rhtml/modifiers';
import { Component, css, html, LitElement } from '@rxdi/lit-html';
import { Animation, ScrollObserver } from '@shared/modifiers';
import { ReactivumTheme } from '@shared/styles/theme';

/**
 * @customElement timeline-component
 */
@Component({
  selector: 'timeline-component',
  styles: [
    css`
      ${ReactivumTheme}

      :host {
        display: block;
        color: var(--text-primary);
      }

      .timeline {
        position: relative;
        padding: 40px 0;
      }

      .timeline::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        width: 3px;
        background: linear-gradient(180deg, var(--primary-color) 0%, var(--secondary-color) 100%);
        transform: translateX(-50%);
        border-radius: 2px;
      }

      .timeline-item {
        position: relative;
        margin-bottom: 60px;
        width: 45%;
        transform: translateZ(0);
        will-change: transform;
        cursor: pointer;
      }

      .timeline-item:nth-child(odd) {
        left: 0;
        text-align: right;
      }

      .timeline-item:nth-child(even) {
        left: 55%;
        text-align: left;
      }

      .timeline-content {
        background: var(--bg-primary);
        border-radius: var(--radius-xl);
        padding: 32px;
        position: relative;
        border: 1px solid var(--border-light);
        box-shadow: var(--shadow-sm);
        transition: all 0.3s ease;
      }

      .timeline-content:hover {
        transform: translateY(-4px);
        box-shadow: var(--shadow-lg);
        border-color: var(--primary-color);
      }

      .timeline-date {
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--primary-color);
        margin-bottom: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
      }

      .timeline-title {
        font-size: 1.3rem;
        font-weight: 600;
        margin-bottom: 12px;
        color: var(--text-primary);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
      }

      .timeline-description {
        color: var(--text-secondary);
        line-height: 1.6;
        font-weight: 400;
      }

      @media (max-width: 768px) {
        .timeline::before {
          left: 20px;
        }

        .timeline-item {
          width: auto;
          left: 0 !important;
          text-align: left !important;
          padding-left: 50px;
        }

        .timeline-item:nth-child(odd) .timeline-content::after,
        .timeline-item:nth-child(even) .timeline-content::after {
          left: -10px !important;
          right: auto !important;
          border-right: 10px solid var(--border-light) !important;
          border-left: none !important;
        }

        .timeline-content {
          padding: 24px;
        }
      }
    `,
  ],
  modifiers: [...FlexLayout, ...AngularLayout, Animation, ScrollObserver],
  template() {
    return html`
      <section class="section" id="timeline" scroll-observer threshold="0.2">
        <div class="container">
          <h2 class="section-title" animated="fadeInDown" delay="0.1s">Our Journey</h2>
          <div class="timeline">
            <div class="timeline-item" animated="slideInLeft" delay="0.2s">
              <div class="timeline-content">
                <div class="timeline-date">2016-2017</div>
                <div class="timeline-title">The Birth of @rxdi</div>
                <div class="timeline-description">
                  Created the foundation with @rxdi - a reactive dependency injection container that works both in
                  browser and Node.js environments, inspired by Angular's DI but with reactive programming.
                </div>
              </div>
            </div>

            <div class="timeline-item" animated="slideInRight" delay="0.3s">
              <div class="timeline-content">
                <div class="timeline-date">2017</div>
                <div class="timeline-title">@gapi Framework</div>
                <div class="timeline-description">
                  Built @gapi on top of @rxdi - a GraphQL API framework designed to provide complex Node.js backend
                  applications with minimum effort, featuring decorators and dependency injection.
                </div>
              </div>
            </div>

            <div class="timeline-item" animated="slideInLeft" delay="0.4s">
              <div class="timeline-content">
                <div class="timeline-date">2019</div>
                <div class="timeline-title">@rhtml Revolution</div>
                <div class="timeline-description">
                  Created @rhtml - the world's smallest dependency injection at less than 1.4kb (0.3kb bundled). This
                  pure and fast DI system became the foundation for modern web component development.
                </div>
              </div>
            </div>

            <div class="timeline-item" animated="slideInRight" delay="0.5s">
              <div class="timeline-content">
                <div class="timeline-date">2020-2021</div>
                <div class="timeline-title">@Component & Abstractions</div>
                <div class="timeline-description">
                  Developed @Component decorator for web components and created powerful abstractions with @modifiers
                  and @custom-attributes, enabling declarative programming patterns.
                </div>
              </div>
            </div>

            <div class="timeline-item" animated="slideInLeft" delay="0.6s">
              <div class="timeline-content">
                <div class="timeline-date">2021-2022</div>
                <div class="timeline-title">Monadic Operators</div>
                <div class="timeline-description">
                  Pioneered the first declarative web component programming pattern using monadic approach. Created
                  operators like r-let and r-if for faster loading and Server Side Rendering.
                </div>
              </div>
            </div>

            <div class="timeline-item" animated="slideInRight" delay="0.7s">
              <div class="timeline-content">
                <div class="timeline-date">2022-Present</div>
                <div class="timeline-title">graphql-server.com</div>
                <div class="timeline-description">
                  Launched our flagship platform combining Agile/Kanban project management with instant GraphQL/REST
                  backend generation using custom serverless architecture on Kubernetes.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class TimelineComponent extends LitElement {}
